import React, { FC } from 'react';
import { Heading, Flex, useTheme } from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { NavLink } from 'react-router-dom';
import styled from '@emotion/styled/macro';
import { breakpoints as mq } from 'styles';
import { rem } from 'utils/fonts';
import useBreakpoint from 'hooks/useBreakpoint';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { useSelector } from 'react-redux';

const BoxHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;

  sub {
    bottom: -2px;
    font-size: 70%;
  }
`;

const StyledHeading = styled(Heading)`
  display: flex;
  justify-content: space-between;
`;

const NavItem = styled(NavLink)`
  display: inline-flex;
  position: relative;
  padding: 0 2px;
  align-items: center;
  transition: all 0.2s ease-in-out;
  justify-content: center;
  font-size: ${rem(14)};

  > svg {
    margin-left: 11px;
  }

  ${mq.md} {
    display: inline-flex;
  }
`;

interface Props {
  title?: string;
  linkText?: string;
  linkMobileText?: string;
  linkUrl?: string;
  showLinkOnMobile?: boolean;
  lightVariant?: boolean;
  boldVariant?: boolean;
  isLinkVisible?: boolean;
  noLinkOnSmallDesktop?: boolean;
  onClick?: () => void;
}

const BoxHeader: FC<Props> = ({
  children,
  title,
  linkText,
  linkMobileText,
  linkUrl,
  showLinkOnMobile = false,
  noLinkOnSmallDesktop = false,
  lightVariant,
  isLinkVisible,
  boldVariant = true,
  onClick,
}) => {
  const { isMobile, isDesktop } = useBreakpoint();
  const { colors } = useTheme();
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  let linkDisplayProp = 'inline-flex';

  if ((isMobile && !showLinkOnMobile) || (!isDesktop && noLinkOnSmallDesktop)) {
    linkDisplayProp = 'none';
  }

  const getColor = () => {
    if (lightVariant) {
      return colors.white;
    }

    if (isCorporateTheme) {
      return colors.secondary[500];
    }

    return colors.secondary[800];
  };

  return (
    <BoxHeaderWrapper>
      <StyledHeading
        fontSize={isCorporateTheme ? rem('14px') : [rem('19px'), rem('18px'), rem('18px'), rem('17px'), rem('18px')]}
        w={linkUrl ? 'auto' : '100%'}
        color={lightVariant ? colors.white : colors.gray[900]}
        fontWeight={isCorporateTheme ? 500 : 'bold'}
      >
        {title ? title : children}
      </StyledHeading>
      {linkUrl && (
        <Flex
          display={linkDisplayProp}
          color={getColor()}
          opacity={isLinkVisible || isLinkVisible === undefined ? 1 : 0}
          transition="opacity 0.1s"
          fontWeight={boldVariant ? '600' : '500'}
          _hover={{
            color: isCorporateTheme ? colors.primary[500] : '',
            textDecoration: isCorporateTheme ? 'none' : 'underline',
          }}
        >
          <NavItem exact to={linkUrl} onClick={onClick}>
            {linkMobileText && isMobile ? linkMobileText : linkText}
            <ArrowForwardIcon boxSize="18px" />
          </NavItem>
        </Flex>
      )}
    </BoxHeaderWrapper>
  );
};

export default BoxHeader;
